<template>
  <div class="sky__home-slider">
    <div
      v-for="(slide, index) in slider"
      :key="index"
      class="sky__home-slide"
      :class="{
        active: index === slideIndex
      }"
    >
      <transition name="img-transition">
        <div
          v-show="index === slideIndex"
          class="sky__slider-img"
        >
          <template v-if="vimeoEmbedUrl(slide)">
            <div class="sky__slider-video-container">
              <iframe
                v-if="videoReady[index]"
                :src="vimeoEmbedUrl(slide)"
                class="sky__slider-video-iframe"
                allow="autoplay; fullscreen"
                allowfullscreen
                frameborder="0"
                playsinline
                @load="onVideoLoad(index)"
              />
              <img
                :src="videoPosterUrl(slide)"
                :alt="slide.attributes.title || 'Video promotionnelle SkyLantern'"
                :fetchpriority="index === 0 ? 'high' : 'low'"
                :loading="index === 0 ? 'eager' : 'lazy'"
                class="sky__slider-video-poster"
                :class="{ 'is-hidden': videoPlaying[index] }"
              >
            </div>
          </template>
          <img
            v-else
            :src="`${slide.attributes.big_picture}?resize=true&width=1500&format=webp`"
            :alt="slide.attributes.title || 'Image promotionnelle SkyLantern'"
            :fetchpriority="index === 0 ? 'high' : 'low'"
            :loading="index === 0 ? 'eager' : 'lazy'"
            class="sky__slider-img-inner"
          >
        </div>
      </transition>
      <div class="sky__slider-tile-wrapper">
        <transition name="tile-transition">
          <div
            v-show="index === slideIndex"
            class="sky__slider-tile flex flex-col md:gap-12"
          >
            <div class="sky__slider-tile-title font-geologica font-light text-center tracking-wide mb-4 text-white uppercase">
              {{ slide.attributes.title }}
            </div>
            <div class="flex justify-center">
              <sky-btn
                v-if="slide.attributes.button_text && slide.attributes.link"
                :href="slide.attributes.link[0] === '/' ? undefined : slide.attributes.link"
                :to="slide.attributes.link[0] === '/' ? slide.attributes.link : undefined"
                class="border-white text-white"
                border-color="white"
                small
              >
                <span class="md:text-2xl text-xl font-geologica font-normal block px-4">{{ slide.attributes.button_text }}</span>
              </sky-btn>
            </div>

          </div>
        </transition>
      </div>
    </div>
    <div class="absolute left-0 right-0 bottom-0 flex items-end justify-center z-20">
      <div class="sky__slider-navigation">
        <div
          v-for="(i, index) in slider.length"
          :key="i"
          class="sky__slider-navigation-item"
          :class="{
            active: index === slideIndex
          }"
          @click="indexWasClicked(index)"
        />
      </div>
    </div>

  </div>
</template>

<script>
// Duree d'affichage d'un slide avant rotation. A ne pas confondre avec le
// 10s du mounted, qui est un filet de securite au demarrage, pas une duree.
const SLIDE_DURATION_MS = 8000

export default {
  props: {
    slider: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      background: require('../../assets/fixtures/slider-img.jpg'),
      slideIndex: 0,
      intervalFn: null,
      videoReady: {},
      videoPlaying: {}
    }
  },
  mounted () {
    const firstSlideHasVideo = this.slider[0] && this.vimeoEmbedUrl(this.slider[0])
    if (firstSlideHasVideo) {
      this.$set(this.videoReady, 0, true)
      setTimeout(() => {
        if (!this.intervalFn) {
          this.setInterval()
        }
      }, 10000)
    } else {
      this.setInterval()
    }
    setTimeout(() => {
      this.slider.forEach((slide, index) => {
        if (index > 0 && this.vimeoEmbedUrl(slide)) {
          this.$set(this.videoReady, index, true)
        }
      })
    }, 2000)
  },
  beforeDestroy () {
    if (this.intervalFn) {
      clearInterval(this.intervalFn)
    }
  },
  methods: {
    vimeoEmbedUrl (slide) {
      const url = slide.attributes.video_url
      if (!url) return null
      const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-f0-9]+))?/)
      if (!match) return null
      const videoId = match[1]
      const hash = match[2]
      let embedUrl = `https://player.vimeo.com/video/${videoId}?background=1&dnt=1`
      if (hash) {
        embedUrl += `&h=${hash}`
      }
      return embedUrl
    },
    videoPosterUrl (slide) {
      return slide.attributes.video_poster || `${slide.attributes.big_picture}?resize=true&width=1500&format=webp`
    },
    onVideoLoad (index) {
      setTimeout(() => {
        this.$set(this.videoPlaying, index, true)
      }, 2500)
      if (index === 0 && !this.intervalFn) {
        setTimeout(() => {
          this.setInterval()
        }, 2500)
      }
    },
    indexWasClicked (index) {
      this.slideIndex = index
      clearInterval(this.intervalFn)
      this.setInterval()
    },
    setInterval () {
      this.intervalFn = setInterval(() => {
        if (this.slider.length - 1 !== this.slideIndex) {
          this.slideIndex++
        } else {
          this.slideIndex = 0
        }
      }, SLIDE_DURATION_MS)
    }
  }
}
</script>

<style lang="scss" scoped>
.sky__home-slider {
  @apply relative bg-sky_grey_darker z-0;
  width: 100%;
  overflow: hidden;
  @screen md {
    height: calc(100vh - 250px);
  }
  .sky__home-slide {
    @apply hidden;
    &.active {
      @apply block;
    }
    @screen md {
      @apply flex;
      &.active {
        @apply flex;
      }
    }
    .sky__slider-img {
      height: 260px;
      width: 100%;
      @screen md {
        @apply absolute left-0 z-10;
        width: 100%;
        height: calc(100vh - 250px);
        @apply top-0 bottom-0;
      }
      .sky__slider-img-inner {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
      .sky__slider-video-container {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
        container-type: size;
      }
      .sky__slider-video-iframe {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        border: 0;
        // Fallback si les unites cq ne sont pas supportees : la video est
        // letterboxee par le player mais reste plein cadre (sans ces lignes,
        // l'iframe retombe a sa taille intrinseque de 300x150).
        width: 100%;
        height: 100%;
        // Cover : l'iframe garde le 16/9 du player Vimeo et deborde du
        // container, qui la rogne via overflow. Les unites cq se referent au
        // container (la banniere ne fait pas la hauteur du viewport), et max()
        // retient toujours la dimension qui deborde.
        width: max(100cqw, 177.78cqh);
        height: max(100cqh, 56.25cqw);
      }
      .sky__slider-video-poster {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        z-index: 2;
        transition: opacity 0.5s ease;
        &.is-hidden {
          opacity: 0;
          pointer-events: none;
        }
      }
    }
    .sky__slider-tile-wrapper {
      @apply flex absolute top-0 bottom-0 left-0 right-0 items-center justify-center;
      .sky__slider-tile {
        @apply z-20;
        max-width: 700px;
        .sky__slider-tile-title {
          font-size: 1.8rem;
          @screen md {
            font-size: 4rem;
            line-height: 4rem;
          }
        }
      }
    }

    .img-transition-enter-active {
      @apply z-10;
      @screen md {
        animation: big-slide-in 1s;
      }
    }
    .img-transition-leave-active {
      transition-duration: 1s;
      @apply z-0;
    }
    .img-transition-leave-to {
      transform: translateX(0%);
      // @apply z-0;
    }

    .tile-transition-enter-active, .tile-transition-leave-active {
      @screen md {
        transition: all 0.3s;
      }
    }
    .tile-transition-enter-active {
      opacity: 1;
      @apply z-30;
    }
    .tile-transition-leave-active {
      opacity: 1;
      @apply z-20;
    }
    .tile-transition-enter {
      opacity: 0;
      @apply z-30;
    }
    .tile-transition-leave-to {
      opacity: 0;
      @apply z-20;
    }
  }
  .sky__slider-navigation {
    @apply flex mb-5;
    .sky__slider-navigation-item {
      width: 10px;
      height: 10px;
      @apply border border-white mr-2 rounded-full relative cursor-pointer;
      &.active {
        background-color: white;
      }
    }
  }
}

@keyframes big-slide-in {
  0% {
    transform: translateY(-100%);
  }
  50% {
    transform: translateY(5%);
  }
  100% {
    transform: translateY(0%);
  }
}
</style>
